<template>
    <div class="position-relative">
        <!-- Header and Filter Section -->
        <b-card class="mt-4">
            <b-row class="align-items-end">
                <!-- Programme Dropdown -->
                <b-col md="5">
                    <b-form-group label="Choose Programme (Scheme):" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedProgrammeId" :options="programmeOptions" class="form-control-sm"
                            @change="onProgrammeChange" />
                    </b-form-group>
                </b-col>

                <!-- Subgroup Dropdown -->
                <b-col md="5">
                    <b-form-group :label="`Choose ${subgroupLabel}:`" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedSubgroupId" :options="subgroupOptions" class="form-control-sm"
                            @change="onSubgroupChange" />
                    </b-form-group>
                </b-col>

                <!-- Go Button -->
                <b-col md="2">
                    <b-button variant="primary" size="sm" class="w-100 font-weight-medium" @click="fetchStudentFees"
                        :disabled="!selectedProgrammeId || !selectedSubgroupId">
                        Go
                    </b-button>
                </b-col>
            </b-row>
        </b-card>

        <!-- Student Table -->
        <div v-if="studentList.length">
            <!-- Error Alert -->
            <div v-if="formError" class="alert alert-danger mb-3">{{ formError }}</div>

            <!-- Update Status -->
            <span v-if="updationEnable" class="text-success font-weight-bold ml-2">
                Updation In Progress... <br />
            </span>

            <!-- Generate Ledger Section -->

            <div class="text-right mt-2">
               <b-button v-if="!ledgersGeneratedForAll && allCategoriesSelected && updateOldData" variant="success"
                    @click="generateLedger" :disabled="updationEnable">
                    Generate Ledger
                </b-button>
                <div v-if="ledgersGeneratedForAll" class="text-success font-weight-bold">
                    Ledger has been generated for all students.
                </div> 
            </div>
            <br>

            <!-- Table with Students -->
                <table class="table table-striped table-bordered">
                    <thead class="thead-dark">
                        <tr>
                            <th>SI. No.</th>
                            <th>Student Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th>Category <span class="text-danger">*</span></th>
                            <th>Components</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(student, index) in studentList" :key="index">
                            <td>{{ index + 1 }}</td>
                            <td>{{ student.text }}</td>
                            <td>{{ student.email }}</td>
                            <td>{{ student.mobile }}</td>

                            <!-- Category Selector -->
                            <td class="d-flex align-items-center">
                                <div class="flex-grow-1 position-relative">
                                    <b-form-select v-model="student.category" :options="categoryOptions"
                                        class="form-control" :disabled="student.transactions && student.transactions.length > 0"
                                        @change="() => {
                                            updateOldData = false;
                                            $nextTick(() => onCategoryChange(student));
                                        }" />
                                    <span v-if="!student.category" class="text-danger">Category is required.</span>
                                </div>

                                <!-- Lock icon with tooltip -->
                                <div v-if="student.transactions && student.transactions.length > 0" class="ms-2 tooltip-wrapper">
                                    <b-icon icon="lock-fill" variant="danger"></b-icon>
                                    <div class="custom-tooltip">
                                        <span>Ledger Generated</span>
                                    </div>
                                </div>
                            </td>

                            <td>
                                <div class="tooltip-wrapper">
                                    {{ student.components }}
                                    <div class="custom-tooltip">
                                        {{ Array.isArray(student.component_amounts) ?
                                            student.component_amounts.join(',') : student.component_amounts }}
                                    </div>
                                </div>

                                <span v-if="!student.category" class="text-danger d-block">
                                    Please select a category to view components.
                                </span>
                            </td>

                            <td>
                                {{ student.amount }}
                                <span v-if="!student.category" class="text-danger">Please select a category to view
                                    amount.</span>
                            </td>

                        </tr>
                    </tbody>
                </table>
           

            <!-- Update Button -->
            <div class="text-right mt-3">
                <b-button variant="primary" v-if="!ledgersGeneratedForAll" :disabled="updationEnable"
                    @click="updateStudentFeeManagement">
                    Update
                </b-button>
            </div>
        </div>

        <div v-else>
            <div v-if="submitted && selectedSubgroupId && studentList.length === 0" class="d-flex justify-content-center mt-3">
                <span class="text-danger">Students not found.</span>
            </div>
        </div>


    </div>
</template>

<script>
export default {
    data() {
        return {
            submitted: false,
            // Selected dropdown values
            selectedProgrammeId: null,
            selectedSubgroupId: null,

            // Display label for subgroup
            subgroupLabel: '',

            // Options for dropdowns
            programmeOptions: [{ value: null, text: 'Select an option' }],
            subgroupOptions: [{ value: null, text: 'Select an option' }],
            categoryOptions: [],

            // Student data
            studentList: [],
            studentFeeList: [],

            // Fee category data
            feeCategories: [],

            // Form state
            formError: '',
            updateOldData: true,
            updationEnable: false,
            allCategoriesSelected: false,
            ledgersGeneratedForAll: false

        };
    },

    async created() {
        // Set subgroup label using custom naming from store
        this.subgroupLabel = this.$store.getters.getCustomNamings.userSubgroup.custom_name || 'Usersubgroup';

        // Load programmes and fee categories initially
        await this.loadProgrammes();
        await this.loadFeeCategories();
    },


    watch: {
        studentList: {
            handler(newVal) {
                if (this.updateOldData) {
                    newVal.forEach((student) => {
                        const feeData = this.studentFeeList[student.value];
                        if (feeData) {
                            // Only set values if category is not already selected manually
                            if (!student.category) {
                                student.fee_student_category_mapping_id = feeData.id;
                                student.transactions = feeData.transactions;
                                student.category = feeData.feecategory.id;
                                student.components = feeData.feecategory.components
                                    .map((c) => `${c.name}`)
                                    .join(', ');
                                student.component_amounts = feeData.feecategory.components
                                    .map((c) => `${c.amount}`)
                                    .join(', ');
                                student.amount = feeData.feecategory.components.reduce(
                                        (sum, c) => sum + parseFloat(c.amount || 0),
                                        0
                                    );
                            }
                        }
                    });
                    this.allCategoriesSelected = this.studentList.every(student => student.category !== null && student.category !== undefined);
                    this.ledgersGeneratedForAll = this.studentList.every(student => student.transactions !== null && student.transactions.length > 0);
                }
            },
            deep: true,
        },
    },

    methods: {
        async generateLedger() {

            this.formError = '';

            if (!this.ledgersGeneratedForAll && !this.allCategoriesSelected || !this.updateOldData) {
                this.formError = 'Please ensure all students have a category selected and data is updated.';
                return;
            }

            this.updationEnable = true;

            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/generate-ledger`;
                const payload = {

                    students: this.studentList
                        .filter(student => !student.transactions || student.transactions.length === 0)
                        .map(student => ({
                            fee_student_category_mapping_id: student.fee_student_category_mapping_id,
                            institution_user_id: student.value,
                            amount: student.amount
                        })),

                };

                const response = await this.$axios.post(url, payload);

                if (response.data.success) {
                    this.$toast.success('Ledger generated successfully.', {
                        position: 'top',
                        duration: 4000,
                    });
                    await this.fetchStudentFees();
                }
            } catch (error) {
                error;
                this.formError = 'Failed to generate ledger. Please try again.';
            } finally {
                this.updationEnable = false;
            }
        },
        // Handle programme dropdown change
        onProgrammeChange() {
            this.selectedSubgroupId = null;
            this.studentList = [];
            this.studentFeeList = [];
            this.loadSubgroups();
        },

        // Handle subgroup change
        onSubgroupChange() {
            this.submitted = false;
            this.studentList = [];
            this.studentFeeList = [];
        },

        // Fetch student fee data and load student list
        async fetchStudentFees() {
            this.submitted = true; // Mark that the Go button was clicked
            const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymapping?subgroup_id=${this.selectedSubgroupId}`;
            try {
                const response = await this.$axios.get(url);
                this.studentFeeList = response.data;
                await this.loadStudents();
            } catch (error) {
                error;
            }
        },

        // Submit updated fee info
        async updateStudentFeeManagement() {
            this.formError = '';
            const invalid = this.studentList.filter((student) => !student.category);
            if (invalid.length) {
                this.formError = 'Please select a category for all students.';
                return;
            }

            this.updationEnable = true;

            const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymapping`;
            const payload = this.studentList.map((student) => ({
                institution_user_id: student.value,
                fee_category_id: student.category,
                fee_student_category_mapping_id: student.fee_student_category_mapping_id || null,
            }));

            try {
                await this.$axios.post(url, { data: payload });
                this.$toast.success('Student fee data submitted successfully.', {
                    position: 'top',
                    duration: 4000,
                });
                this.updateOldData = true;
                await this.fetchStudentFees();
            } catch (error) {
                this.formError = 'Failed to submit. Please try again.';
            } finally {
                this.updationEnable = false;
            }
        },

        // Load programme options
        async loadProgrammes() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups?allusergroups=1`;
            const response = await this.$axios.get(url);
            this.programmeOptions = [
                { value: null, text: 'Select an option' },
                ...response.data.map((programme) => ({
                    value: programme.id,
                    text: `${programme.name} (${programme.code}) ${programme.scheme || ''}`,
                })),
            ];
        },

        // Load subgroup options for selected programme
        async loadSubgroups() {
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups/${this.selectedProgrammeId}/usersubgroups?academic_year=${this.$root.default_academic_year[0]}`;
                const response = await this.$axios.get(url);
                this.subgroupOptions = [
                    { value: null, text: 'Select an option' },
                    ...response.data
                        .filter(subgroup => subgroup.type === null)
                        .map(subgroup => ({
                            value: subgroup.id,
                            text: `${subgroup.name} (${subgroup.code})`
                        }))
                ];
            } catch (error) {
                error;
                this.subgroupOptions = [{ value: null, text: 'Error loading options' }];
            }
        },

        // Load student users from selected subgroup
        async loadStudents() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/usersubgroup/${this.selectedSubgroupId}/users`;
            const response = await this.$axios.get(url);
            this.studentList = response.data
                .filter((user) => user.pivot.usersubgrouprole_id === 2)
                .map((user) => ({
                    value: user.pivot.institution_user_id,
                    text: `${user.first_name} ${user.last_name}`,
                    email: user.email,
                    mobile: user.mobile,
                    transactions: [],
                    category: null,
                    components: '',
                    amount: '',
                }));
        },

        // Load fee categories with components
        async loadFeeCategories() {
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/feecategories?with_components=1`;
                const response = await this.$axios.get(url);
                this.feeCategories = response.data;
                this.categoryOptions = [
                    { value: null, text: 'Select Category' },
                    ...this.feeCategories.map((cat) => ({
                        value: cat.id,
                        text: cat.name,
                    })),
                ];
            } catch (error) {
                error;
            }
        },

        // On fee category change for student
        onCategoryChange(student) {
            const selected = this.feeCategories.find((cat) => cat.id === student.category);
            console.log("selected",selected);
            if (selected) {
                student.components = selected.components
                    .map((c) => `${c.name}`)
                    .join(', ');
                student.component_amounts = selected.components
                    .map((c) => `${c.amount}`)
                    .join(', ');
                student.amount = selected.components.reduce(
                    (sum, c) => sum + parseFloat(c.amount || 0),
                    0
                );
            } else {
                student.components = '';
                student.amount = '';
            }
            this.allCategoriesSelected = this.studentList.every(student => student.category !== null && student.category !== undefined);
            this.ledgersGeneratedForAll = this.studentList.every(student => student.transactions !== null && student.transactions.length > 0);
        },
    },
};
</script>

<style scoped>
/* Red asterisk for required labels */
.required-label::after {
    content: '*';
    color: red;
    margin-left: 4px;
}

.tooltip-wrapper {
    position: relative;
    display: inline-block;
    cursor: pointer;
}

.custom-tooltip {
    visibility: hidden;
    background-color: #000;
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    position: absolute;
    z-index: 999;
    top: 100%;
    /* position below */
    left: 0;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.3s;
    margin-top: 5px;
    font-size: 13px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-wrapper:hover .custom-tooltip {
    visibility: visible;
    opacity: 1;
}
</style>
